(() => {
  const controllers = new Map();
  const mobile = window.matchMedia('(max-width: 760px)');
  const designMode = Boolean(window.Shopify?.designMode);
  const memory = new Map();
  const read = (storage, key) => {
    try { return window[storage].getItem(key); } catch (_) { return memory.get(storage + ':' + key) || null; }
  };
  const write = (storage, key, value) => {
    memory.set(storage + ':' + key, value);
    try { window[storage].setItem(key, value); } catch (_) { /* Storage can be unavailable in private browsers. */ }
  };
  const syncScroll = () => document.body.classList.toggle('cl-welcome-open', Boolean(document.querySelector('.cl-welcome[open]')));
  const initialize = (root) => {
    if (controllers.has(root)) return;
    const dialog = root.querySelector('.cl-welcome');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const result = root.querySelector('[data-welcome-result]');
    const state = result?.dataset.welcomeResult || 'signup';
    const confirmed = result?.dataset.confirmed === 'true';
    const storageKey = root.dataset.storageKey;
    const sessionKey = storageKey + ':dismissed';
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let timer = null;
    let opener = null;
    let automaticDone = false;
    const stored = () => {
      try { return JSON.parse(read('localStorage', storageKey) || '{}'); } catch (_) { return {}; }
    };
    const remember = (values) => {
      if (designMode) return;
      write('localStorage', storageKey, JSON.stringify(Object.assign(stored(), values)));
    };
    const eligibleDevice = () => root.dataset.devices === 'all' ||
      (root.dataset.devices === 'mobile' && mobile.matches) ||
      (root.dataset.devices === 'desktop' && !mobile.matches);
    const eligible = () => {
      if (root.dataset.enabled !== 'true' || !root.isConnected) return false;
      if (designMode) return true;
      if (!eligibleDevice()) return false;
      // A real Shopify form result must remain visible, even after a previous dismissal.
      if (confirmed || state === 'error') return true;
      if (root.dataset.pageAllowed !== 'true' || root.dataset.audience !== 'true') return false;
      const saved = stored();
      if (root.dataset.hideSubscribers === 'true' && saved.subscribed) return false;
      if (read('sessionStorage', sessionKey) === 'true') return false;
      const days = Number(root.dataset.repeatDays) || 0;
      return !(days > 0 && saved.dismissedAt && Date.now() - saved.dismissedAt < days * 86400000);
    };
    const otherOverlay = () => Boolean(document.querySelector('dialog[open]:not(.cl-welcome), .cl-header[data-menu-open="true"]'));
    const open = (force = false) => {
      if (dialog.open || (!force && !eligible())) return false;
      if (!force && (document.hidden || otherOverlay())) return false;
      opener = document.activeElement;
      document.dispatchEvent(new CustomEvent('cl:close-menu'));
      dialog.showModal();
      syncScroll();
      const target = state === 'success'
        ? root.querySelector('.cl-welcome__title')
        : (state === 'error' ? root.querySelector('.cl-welcome__email') : root.querySelector('[data-welcome-close]'));
      target?.focus({ preventScroll: true });
      return true;
    };
    const close = () => { if (dialog.open) dialog.close(); };
    const schedule = (delay) => {
      window.clearTimeout(timer);
      if (automaticDone || !eligible()) return;
      timer = window.setTimeout(() => {
        if (!eligible()) return;
        if (open()) automaticDone = true;
        else schedule(700);
      }, delay);
    };
    const dismissed = () => {
      if (!designMode) {
        remember({ dismissedAt: Date.now() });
        write('sessionStorage', sessionKey, 'true');
      }
      automaticDone = true;
      window.clearTimeout(timer);
      syncScroll();
      if (opener?.isConnected && !document.querySelector('dialog[open]')) opener.focus({ preventScroll: true });
    };

    if (confirmed) remember({ subscribed: true });
    dialog.addEventListener('close', dismissed, options);
    root.querySelectorAll('[data-welcome-close]').forEach(button => button.addEventListener('click', close, options));
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
    }, options);
    root.querySelector('.cl-welcome__form')?.addEventListener('submit', event => {
      // Keep Shopify's normal POST and CAPTCHA flow. No simulated success or AJAX interception.
      if (designMode) { event.preventDefault(); return; }
    }, options);
    const copy = root.querySelector('[data-welcome-copy]');
    copy?.addEventListener('click', async () => {
      const code = root.querySelector('[data-welcome-code]');
      if (!code) return;
      let copied = false;
      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        copied = true;
      } catch (_) {
        const field = document.createElement('textarea');
        field.value = code.textContent.trim();
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;opacity:0;width:1px;height:1px;';
        dialog.appendChild(field);
        field.select();
        try { copied = document.execCommand('copy'); } catch (_) { /* Leave the code selectable. */ }
        field.remove();
        if (!copied) {
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }
      if (copied) {
        copy.textContent = copy.dataset.copiedLabel;
        const status = root.querySelector('[data-welcome-copy-status]');
        if (status) status.textContent = copy.dataset.copiedLabel;
      }
      copy.focus({ preventScroll: true });
    }, options);
    const controller = {
      open: () => open(true),
      close,
      reschedule: () => schedule(Number(root.dataset.delay) * 1000 || 0),
      destroy: () => {
        window.clearTimeout(timer);
        abort.abort();
        close();
        controllers.delete(root);
        syncScroll();
      }
    };
    controllers.set(root, controller);
    schedule(designMode || confirmed || state === 'error' ? 150 : (Number(root.dataset.delay) || 0) * 1000);
  };
  const initializeWithin = (container) => {
    if (container.matches?.('[data-welcome-root]')) initialize(container);
    container.querySelectorAll('[data-welcome-root]').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, root) => { if (event.target.contains(root)) controller.destroy(); });
  });
  document.addEventListener('shopify:section:select', event => {
    controllers.forEach((controller, root) => { if (event.target.contains(root)) controller.open(); });
  });
  document.addEventListener('shopify:section:deselect', event => {
    controllers.forEach((controller, root) => { if (event.target.contains(root)) controller.close(); });
  });
  mobile.addEventListener('change', () => {
    controllers.forEach((controller, root) => {
      const deviceAllowed = root.dataset.devices === 'all' ||
        (root.dataset.devices === 'mobile' && mobile.matches) ||
        (root.dataset.devices === 'desktop' && !mobile.matches);
      if (!designMode && !deviceAllowed) controller.close();
      controller.reschedule();
    });
  });
  window.addEventListener('pageshow', syncScroll);
})();
